import { Formik, FormikProps } from 'formik';
import { ScrollToFieldError } from '@synergy/components';
import { Stack } from '@mui/material';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { forwardRef, useEffect, useRef } from 'react';
import { useParams } from 'react-router';

import * as SupplierDetailConditionsActions from './store/supplier-conditions-actions';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { SupplierConditionsFormValues } from './models/supplier-conditions-form-values';
import { SupplierDetailConditionsBonusConditions } from './bonus-conditions/supplier-detail-conditions-bonus-conditions';
import { SupplierDetailConditionsContract } from './components/supplier-detail-conditions-contract';
import { SupplierDetailConditionsDeliveryTerms } from './components/supplier-detail-conditions-delivery-terms';
import { SupplierDetailConditionsPayment } from './components/supplier-detail-conditions-payment';
import { SupplierDetailConditionsPurchaseTerms } from './components/supplier-detail-conditions-purchase-terms';
import {
    supplierConditionsSelector,
    supplierDetailConditionsBaselineSelector,
    supplierDetailConditionsFormDataSelector,
} from './store/supplier-conditions-selectors';
import { supplierDetailConditionsValidationSchema } from './supplier-detail-conditions-validation-schema';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const SupplierDetailConditions = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const formikRef = useRef<FormikProps<SupplierConditionsFormValues>>(null);

    const { formValues, errors, touched } = useAppSelector(supplierDetailConditionsFormDataSelector);

    // The conditions as the server has them — see PaloDirtyComparison.

    const baseline = useAppSelector(supplierDetailConditionsBaselineSelector);
    const supplierConditions = useAppSelector(supplierConditionsSelector);
    const dispatch = useThunkDispatch();

    useEffect(() => {
        if (supplierIdParam && +supplierIdParam && !supplierConditions.detailConditions) {
            dispatch(SupplierDetailConditionsActions.fetchSupplierConditions(+supplierIdParam));
        }
    }, [dispatch, supplierConditions.detailConditions, supplierIdParam]);

    // On every opening, not only with the conditions: the mask fetches those
    // with the record (#2212), and gated on them the options never came — the
    // field found no option for the stored term and stood empty (#2326 R-242).
    useEffect(() => {
        dispatch(SupplierDetailConditionsActions.fetchDeliveryTerms());
    }, [dispatch]);

    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(SupplierDetailConditionsActions.updateSupplierConditions(values, isDirty, isValid, errs));
    });

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            initialValues={formValues}
            validationSchema={supplierDetailConditionsValidationSchema}
            enableReinitialize={true}
            initialErrors={errors}
            initialTouched={touched}
            innerRef={formikRef}
        >
            <Stack gap={6}>
                <PaloDirtyComparison baseline={baseline} />
                <ScrollToFieldError />
                <SupplierDetailConditionsPurchaseTerms />
                <SupplierDetailConditionsPayment />
                <SupplierDetailConditionsDeliveryTerms />
                <SupplierDetailConditionsContract />
                <SupplierDetailConditionsBonusConditions />
            </Stack>
        </Formik>
    );
});
